import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { GlowWrapper } from "@/components/ui/glow-wrapper";

export function ClinicalValidationSection() {
  const endorsements = [
    {
      id: "mnda-sa",
      icon: "🩺",
      title: "DR. LIENKA BOTHA",
      subtitle: "Clinical Lead, Motor Neurone Disease Association of South Africa",
      quote: "Such a platform would give us earlier identification of diagnostic delays, visibility of access to essential interventions, better understanding of symptom progression, and improved coordination between advisers, clinicians, and families. This is not just technology – it's a lifeline for families navigating MND.",
      points: [
        "Earlier identification of diagnostic delays in provinces and districts",
        "Visibility of access to essential interventions (respiratory support, nutrition, communication aids)",
        "Better understanding of symptom progression and caregiver burden",
        "Improved coordination between MNDA SA Lay Advisors, clinicians, and families",
        "Data that strengthens advocacy efforts and informs national planning",
        "More efficient allocation of scarce resources and donated equipment"
      ],
      conclusion: "Such a platform would not replace the human support families rely on, but it would enhance it. It would provide us with what we currently lack: accurate, local, and longitudinal information that helps us direct support where it is most needed.",
      cta: "Read Full Letter of Intent from MNDA SA",
      color: "teal",
      gradientColors: ["rgba(20, 184, 166, 0.35)", "rgba(6, 182, 212, 0.30)"]
    },
    {
      id: "dr-visser",
      icon: "🧠",
      title: "DR. MICHAEL VISSER",
      subtitle: "Clinical Education at University of Queensland",
      quote: "Through her company Elevita AI, Rebecca has built an end-to-end digital health platform that bridges long-standing gaps in early triage, data standardisation, research integrity, patient safety, and advocacy. Grounded in her own experience as a patient and carer, Elevita redefines what 'Partnering with Consumers' means — transforming it from a compliance requirement into a genuine collaboration between clinicians, patients, and policymakers.",
      points: [],
      conclusion: "",
      cta: "Read Queensland Health Recognition",
      color: "purple",
      gradientColors: ["rgba(168, 85, 247, 0.35)", "rgba(147, 51, 234, 0.30)"]
    }
  ];

  const getBgColor = (color: string) => {
    switch (color) {
      case "teal": return "bg-gradient-to-br from-teal-50/80 to-teal-100/60 dark:from-teal-950/80 dark:to-teal-900/60";
      case "purple": return "bg-gradient-to-br from-purple-50/80 to-purple-100/60 dark:from-purple-950/80 dark:to-purple-900/60";
      default: return "bg-gray-50 dark:bg-gray-950";
    }
  };

  const getTextColor = (color: string) => {
    switch (color) {
      case "teal": return "text-teal-800 dark:text-teal-200";
      case "purple": return "text-purple-800 dark:text-purple-200";
      default: return "text-gray-800 dark:text-gray-200";
    }
  };

  return (
    <section id="clinical-validation" className="py-20 relative z-10 bg-white dark:bg-black">
      <div className="container mx-auto px-6">
        <motion.div 
          className="text-center mb-16 text-black dark:text-white"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            CLINICAL VALIDATION: <span className="text-teal-600 dark:text-teal-400">WHY EXPERTS SUPPORT CURALYSIS</span>
          </h2>
          <div className="w-24 h-1 bg-teal-600 dark:bg-teal-400 mx-auto mb-8"></div>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            Leading healthcare professionals and organisations endorse our approach to transforming neurological care.
          </p>
        </motion.div>

        <div className="space-y-12">
          {endorsements.map((endorsement, index) => (
            <motion.div
              className="p-8 relative overflow-hidden rounded-lg"
              style={{
                background: `linear-gradient(135deg, ${endorsement.gradientColors[0]} 0%, ${endorsement.gradientColors[1]} 100%)`,
                backdropFilter: 'blur(12px)',
                border: endorsement.color === "teal" 
                  ? '1px solid rgba(20, 184, 166, 0.3)'
                  : '1px solid rgba(168, 85, 247, 0.3)',
                boxShadow: endorsement.color === "teal"
                  ? '0 12px 40px rgba(20, 184, 166, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.08)'
                  : '0 12px 40px rgba(168, 85, 247, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.08)'
              }}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, delay: index * 0.3 }}
            >
              {/* Header with icon and title */}
              <div className="flex items-start gap-4 mb-6">
                <span className="text-4xl">{endorsement.icon}</span>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-black dark:text-white mb-2">
                    {endorsement.title}
                  </h3>
                  <p className={`text-lg font-semibold ${getTextColor(endorsement.color)}`}>
                    {endorsement.subtitle}
                  </p>
                </div>
              </div>

              {/* Main quote */}
              <blockquote className="text-lg text-gray-700 dark:text-gray-300 mb-6 leading-relaxed italic">
                "{endorsement.quote}"
              </blockquote>

              {/* Bullet points if any */}
              {endorsement.points.length > 0 && (
                <div className="mb-6">
                  <div className="grid md:grid-cols-2 gap-3">
                    {endorsement.points.map((point, pointIndex) => (
                      <div key={pointIndex} className="flex items-start gap-3">
                        <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${getTextColor(endorsement.color)}`} 
                             style={{ backgroundColor: endorsement.color === "blue" ? "#3B82F6" : "#8B5CF6" }} />
                        <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                          {point}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Conclusion if any */}
              {endorsement.conclusion && (
                <blockquote className="text-base text-gray-700 dark:text-gray-300 mb-2 leading-relaxed italic border-l-4 border-gray-300 dark:border-gray-600 pl-4">
                  {endorsement.conclusion}
                </blockquote>
              )}


              {/* Decorative element */}
              <motion.div
                className="absolute top-4 right-4 w-20 h-20 rounded-full opacity-20 backdrop-blur-sm"
                style={{ backgroundColor: endorsement.color === "teal" ? "#14b8a6" : "#8B5CF6" }}
                animate={{
                  scale: [1, 1.3, 1],
                  opacity: [0.05, 0.1, 0.05]
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  delay: index * 0.7
                }}
              />
            </motion.div>
          ))}
        </div>

        {/* Bottom Section */}
        <motion.div
          className="mt-16 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <div 
            className="p-8 max-w-4xl mx-auto rounded-lg"
            style={{
              background: 'linear-gradient(135deg, rgba(29, 78, 216, 0.12) 0%, rgba(126, 34, 206, 0.15) 100%)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              boxShadow: '0 12px 40px rgba(29, 78, 216, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.08)'
            }}
          >
            <h3 className="text-2xl font-bold text-black dark:text-white mb-4">
              MEET THE FOUNDER: REBECCA KAY-FRANCIS
            </h3>
            <div className="grid md:grid-cols-2 gap-8 items-start">
              <div className="text-left">
                <p className="text-lg text-gray-700 dark:text-gray-300 mb-4 leading-relaxed">
                  Rebecca is a lived-experience innovator from Tully, Far North Queensland, building research-grade healthcare platforms for people living with neurological conditions. Drawing from her own journey with neurodiversity and epilepsy, she founded Curalysis to transform how patients, clinicians, and researchers collaborate.
                </p>
                <p className="text-sm font-semibold text-teal-600 dark:text-teal-400 mb-2">
                  Her work has been recognised by:
                </p>
                <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                  <li>• CSIRO, Impact10x, TRW innovation programs</li>
                  <li>• Judges' Choice & Social Impact awards</li>
                  <li>• Queensland Health leadership</li>
                  <li>• Motor Neurone Disease Association of South Africa</li>
                </ul>
              </div>
              <div 
                className="rounded-lg p-6"
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.05)'
                }}
              >
                <p className="text-sm font-bold text-teal-600 dark:text-teal-400 mb-3">
                  WHAT EXPERTS SAY ABOUT REBECCA:
                </p>
                <blockquote className="text-sm text-gray-700 dark:text-gray-300 italic leading-relaxed">
                  "Rebecca Kay-Francis' work stands at the intersection of lived experience, ethical design, and systems reform — precisely where the NSQHS 'Partnering with Consumers' standard seeks to embed meaningful change. Her contribution offers an evidence-based, human-centred model for how Queensland can lead the nation in integrating consumer voices not as an afterthought, but as the foundation of care quality and safety."
                </blockquote>
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-3">
                  — Dr. Michael Visser, Clinical Education at University of Queensland
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                  From his letter to the Queensland Premier advocating for state recognition of Rebecca's work.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
